'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import { Check, Loader2 } from 'lucide-react';
import { withBase } from '@/lib/base-path'

type Stage = 'thanking' | 'analyzing' | 'ready';

interface CompletionProgressProps {
  stage: Stage;
  title?: string;
  subtitle?: string;
}

// Shared step indicator rendered both during the post-chat transition and
// while ClientReport polls for output. Removes the "two unrelated loaders"
// UX jank — users see one coherent progression instead of spinner → new
// spinner → report.
export function CompletionProgress({ stage, title, subtitle }: CompletionProgressProps) {
  const steps: { id: Stage; label: string }[] = [
    { id: 'thanking', label: 'Gesprek afgerond' },
    { id: 'analyzing', label: 'Rapport samenstellen' },
    { id: 'ready', label: 'Klaar om te bekijken' },
  ];

  const activeIndex = steps.findIndex((s) => s.id === stage);

  return (
    <div className="flex items-center justify-center min-h-[80vh] bg-off-white">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="px-8 max-w-md w-full"
      >
        <Image src={withBase("/logomark.svg")} alt="" width={36} height={29} className="h-7 w-auto mb-8" />
        <h2 className="text-3xl font-light text-off-black font-display mb-2">
          {title ?? 'Je rapport wordt klaargemaakt'}
        </h2>
        <p className="text-sm text-off-black/50 mb-8">
          {subtitle ?? 'Dit duurt meestal 20 tot 40 seconden. Je kunt dit venster open laten staan.'}
        </p>

        <ol className="text-left space-y-3">
          {steps.map((step, i) => {
            const isDone = i < activeIndex;
            const isActive = i === activeIndex;
            return (
              <li key={step.id} className="flex items-center gap-3">
                <span
                  className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                    isDone
                      ? 'bg-off-black text-off-white'
                      : isActive
                      ? 'bg-orange-cta text-white'
                      : 'bg-off-black/5 text-off-black/40'
                  }`}
                  aria-hidden
                >
                  {isDone ? (
                    <Check className="w-3.5 h-3.5" />
                  ) : isActive ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    i + 1
                  )}
                </span>
                <span
                  className={`text-sm ${
                    isDone
                      ? 'text-off-black/60'
                      : isActive
                      ? 'text-off-black font-medium'
                      : 'text-off-black/40'
                  }`}
                >
                  {step.label}
                </span>
              </li>
            );
          })}
        </ol>
      </motion.div>
    </div>
  );
}
